import dayjs from 'dayjs';

import { createDateAsUTC } from '@utils/date';

export function startOfToday(): Date {
  return dayjs().startOf('day').toDate();
}

/**
 * `draft_release_date` is stored as a UTC-midnight timestamp for a calendar
 * date. Read its UTC year/month/day and rebuild as a local-midnight `Date` so
 * the date field displays the same calendar day it was saved with.
 */
export function parseReleaseDate(value?: string | null): Date | null {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return calendarDateFromUtcParts(parsed);
}

/**
 * Serialize a local-calendar `Date` as UTC midnight ISO for the API.
 */
export function toReleaseDateInput(value: Date | null): string | undefined {
  if (!value) {
    return undefined;
  }

  return createDateAsUTC(dayjs(value).startOf('day').toDate()).toISOString();
}

/**
 * `DateInput` emits a Date whose UTC fields hold the picked calendar day
 * (`createDateAsUTC`), but displays values with local time via `dayjs`.
 * Rebuild as local midnight so the field keeps showing the selected day in
 * timezones behind UTC.
 */
export function normalizeReleaseDate(value: Date | null): Date | null {
  if (!value) {
    return null;
  }

  return calendarDateFromUtcParts(value);
}

function calendarDateFromUtcParts(value: Date): Date {
  return new Date(
    value.getUTCFullYear(),
    value.getUTCMonth(),
    value.getUTCDate()
  );
}

/** Format a form-state release date the same way `DateInput` displays it. */
export function formatReleaseDateForDisplay(value: Date | null): string {
  if (!value) {
    return '';
  }

  return dayjs(value).format('YYYY-MM-DD');
}
